import React from "react";
import { CHAT_COLUMN_CLASS } from "../constants/chatLayout";

interface StatusIndicatorProps {
  status: "thinking" | "tool_selected" | "tool_executing" | "generating" | null;
  toolName?: string;
}

const TOOL_MESSAGES: Record<string, string> = {
  perplexity_search: "Searching the web...",
  // OpenAI's built-in search on GPT models (chat-service emits tool_executing "web_search")
  web_search: "Searching the web...",
  deep_research: "Researching the web — this may take a minute...",
  crawl_website: "Searching the web...",
  schedule_post: "Scheduling your post...",
  claude_blog_writer: "Writing blog content...",
  create_social_post: "Creating social post...",
  output_social_posts: "Creating social posts...",
  generate_image: "Generating images...",
  generate_image_batch: "Generating your image set...",
  list_calls: "Looking up your calls...",
  output_calls: "Preparing your calls list...",
  scan_business_cards: "Reading your cards...",
  draft_card_intros: "Writing the intro emails...",
  output_card_contacts: "Preparing your contacts...",
  review_google_business_profile: "Preparing your Google Business Profile changes...",
  revise_gbp_row: "Updating your proposal...",
  get_reddit_threads: "Reading your Reddit Feed...",
  agent_memory: "Updating memory...",
};

export const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  status,
  toolName,
}) => {
  // Always show indicator if status exists or return null
  if (!status) return null;

  const getStatusMessage = () => {
    switch (status) {
      case "thinking":
        // Always show "Working on it..." for thinking state
        return "Working on it...";
      case "tool_selected":
        return toolName
          ? `Preparing to use ${toolName.replace(/_/g, " ")}...`
          : "Selecting tool...";
      case "tool_executing":
        return toolName && TOOL_MESSAGES[toolName]
          ? TOOL_MESSAGES[toolName]
          : "Processing...";
      case "generating":
        return "Generating response...";
      default:
        return "Working on it...";
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case "thinking":
        return (
          <div className="flex gap-1">
            <span className="h-2 w-2 animate-bounce rounded-full bg-theme-fg-muted"></span>
            <span
              className="h-2 w-2 animate-bounce rounded-full bg-theme-fg-muted"
              style={{ animationDelay: "0.1s" }}
            ></span>
            <span
              className="h-2 w-2 animate-bounce rounded-full bg-theme-fg-muted"
              style={{ animationDelay: "0.2s" }}
            ></span>
          </div>
        );
      case "tool_selected":
      case "tool_executing":
        return (
          <div className="relative w-5 h-5">
            <div className="absolute inset-0 animate-spin rounded-full border-2 border-theme-fg-muted border-t-transparent"></div>
            <div className="absolute inset-1 flex items-center justify-center">
              <svg
                className="h-3 w-3 text-theme-fg"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
          </div>
        );
      case "generating":
        return (
          <div className="flex gap-1">
            <span
              className="h-1.5 w-1.5 animate-pulse rounded-full bg-theme-fg-muted"
              style={{ animationDelay: "0s" }}
            ></span>
            <span
              className="h-1.5 w-1.5 animate-pulse rounded-full bg-theme-fg-muted"
              style={{ animationDelay: "0.15s" }}
            ></span>
            <span
              className="h-1.5 w-1.5 animate-pulse rounded-full bg-theme-fg-muted"
              style={{ animationDelay: "0.3s" }}
            ></span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`${CHAT_COLUMN_CLASS} animate-fadeIn`}>
      <div className="w-full min-w-0">
        <div className="inline-flex items-center gap-2 rounded-2xl border border-theme-border bg-theme-surface-ui px-4 py-2">
          {getStatusIcon()}
          <span className="text-sm font-medium text-theme-fg">
            {getStatusMessage()}
          </span>
        </div>
      </div>
    </div>
  );
};
